<template>
  <div class="rank-list" :style="{width: config.chartWidth+'px',height:config.chartHeight+'px'}">
    <div class="rank-list-wrapper">
      <img v-if="borderImgUrl" class="rank-list-border" :src="borderImgUrl" alt=""
           :style="chartBorderTemplateImageStyle">
      <div class="rank-list-title" :style="titleBarInlineStyle">
        {{ config.chartTitle }}
        <el-tooltip v-if="config.chartViewData.showIndicator" class="item" :open-delay="500" effect="dark" placement="top">
          <div slot="content" v-html="indicatorDescription"></div>
          <i class="el-icon-question"></i>
        </el-tooltip>
      </div>
    </div>
    <div ref="barChart" class="rank-list-container"></div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { COLOR_LIST } from '@/utils/defaultColorData'
import { imgURLtoBase64, getNumberFormat } from '@/utils/common'
import { rankBaseOption, buildRankYAxisLabelRich } from './options'
import ChartMixin from './mixin'

export default {
  mixins: [ChartMixin],

  computed: {
    themeColor () {
      return COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value
    },
    style () {
      return this.config.chartSet
    },
    chartData () {
      return this.config.chartViewData.chartData
    },
    // 维度
    dataAxis () {
      const list = []
      if (this.chartData?.length && Array.isArray(this.chartData)) {
        const colKeyList = Object.keys(this.chartData[0])
        this.chartData.slice(1).forEach(bar => {
          list.push(bar[colKeyList[0]]) // 第一列数据(维度)
        })
        return list.slice(0, this.style.chartStyle.setRankNumber) // 榜单个数
      } else {
        return []
      }
    },
    //  获取指标返回的数据中的最大值（仅认 col_1 数值列）
    dataMax () {
      if (!this.chartData?.length || !Array.isArray(this.chartData)) return 0
      const vals = this.chartData.slice(1)
        .map(row => Number(row.col_1))
        .filter(n => !Number.isNaN(n))
      if (!vals.length) return 0
      const max = Math.max(...vals)
      return max === 0 ? 10 : max
    },
    // 数据
    series () {
      if (!this.chartData?.length) {
        return
      }
      if (!Array.isArray(this.chartData)) {
        return
      }
      const data = {}

      const maxValue = +this.style.chartStyle.max
      // 读取 第1列 & 第2列 的表头key
      const colKeyList = Object.keys(this.chartData[0]).slice(0, 2)
      // 表头
      const tableHeader = this.componentType === 'barPolar' ? '' : this.chartData[0]
      // 表格数据 (去除第一行(表头) 设置榜单个数)
      const tableData = this.chartData.slice(1).splice(0, this.style.chartStyle.setRankNumber)

      // 遍历除第一列(维度)之外的所有列
      colKeyList.forEach(key => {
        // 排除空数据
        if (!tableData.every(row => (row[key] === '' || row[key] === undefined))) {
          data.name = tableHeader[key]
          data.data = tableData.map(bar => bar[key])
          data.max = tableData.map(item => maxValue)
        }
      })
      return data
    },
    // 比率配置
    settingRate () {
      const rateData = {}
      if (this.config.chartSet.chartStyle?.setRate) {
        const colKeyList = ['col_7']
        const tableHeader = this.chartData[0]
        const tableData = this.chartData.slice(1)
        colKeyList.forEach(key => {
        // 排除空数据
          if (!tableData.every(row => (row[key] === '' || row[key] === undefined))) {
            // list.push({
            rateData.name = tableHeader[key] // 当前数据列的表头
            rateData.data = tableData.map(bar => bar[key])// 当前数据列
            // })
          }
        })
      }
      return rateData
    },
    // 获取配置的最大值
    rankMax () {
      return +this.style.chartStyle.max
    },

  },
  watch: {
    // 数据变更时
    chartData: {
      handler: function (val) {
        // 数据变化时：轴最大值取「当前数据最大值」与「侧栏已填最大值」的较大者，避免覆盖用户为留白设的大值
        const dm = Number(this.dataMax) || 0
        const cur = Number(this.style.chartStyle.max)
        const user = Number.isFinite(cur) && cur > 0 ? cur : 0
        this.config.chartSet.chartStyle.max = Math.max(dm, user)
        this.init()
      },
      deep: true,
    },
    resolvedChartColorsLabel () {
      this.style.chartStyle.rateColor = COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value[0]
      this.init()
    },
    // 配置项变更
    config: {
      handler: function () {
        // 配置的最大值发生变化时 与数值的最大值进行比较
        this.config.chartSet.chartStyle.max = this.rankMax > this.dataMax ? this.rankMax : this.dataMax
        this.init()
      },
      deep: true,
    },
    async borderStyle (val) {
      if (val) {
        this.borderImgUrl = await imgURLtoBase64(val)
      } else {
        this.borderImgUrl = ''
      }
    },
  },
  async created () {
    if (this.borderStyle) {
      this.borderImgUrl = await imgURLtoBase64(this.borderStyle)
    }
    if (!this.style.chartStyle?.carouselDelay) { this.$set(this.config.chartSet.chartStyle, 'carouselDelay', 3) }
    if (!this.style.chartStyle?.setRankNumber) { this.$set(this.config.chartSet.chartStyle, 'setRankNumber', 5) }
    if (!this.style.chartStyle?.borderWidth) { this.$set(this.config.chartSet.chartStyle, 'borderWidth', 10) }
    if (!this.style.chartStyle?.borderRadius) { this.$set(this.config.chartSet.chartStyle, 'borderRadius', 10) }

    this.style.chartStyle.rateColor = this.style.chartStyle?.rateColor || COLOR_LIST.filter(item => { return item.label === this.resolvedChartColorsLabel })[0].value[0]
  },
  mounted () {
    if (!this.charts) {
      this.charts = echarts.init(this.$refs.barChart)
    }

    this.init()
  },
  methods: {
    init () {
      const barW = this.style.chartStyle.borderWidth
      const radius = this.style.chartStyle.borderRadius
      const barRadius = [0, radius, radius, 0]
      this.option = _.merge(_.cloneDeep(rankBaseOption), {
        grid: {
          right: this.style.chartStyle.rateType === '' ? '60' : '142',
        },
        color: this.themeColor,
        tooltip: {
          textStyle: {
            fontSize: this.style.chartStyle.fontSize,
          },
          formatter: (params) => {
            const { color, value, seriesName, name } = params
            const str = `${seriesName} <br/>
              <div style="display:inline-block;width:10px;height:10px;background-color:${color}; border-radius:50px;  margin-right: 4px">
              </div><span style="margin-right:10px">${name}</span>
              <span>${getNumberFormat(value)}${this.style.chartStyle.unit}</span> `
            return str
          },

        },
        yAxis: {
          data: this.dataAxis,
          axisLabel: {
            fontSize: this.style.chartStyle.fontSize,
            rich: buildRankYAxisLabelRich(this.style.chartStyle.fontSize),
          },
        },
        xAxis: {
          max: this.style.chartStyle.max,
        },
        series: [
          {
            name: this.series?.name,
            data: this.series?.data,
            barWidth: barW,
            itemStyle: {
              borderRadius: barRadius,
            },
          },
          {
            name: this.series?.name,
            barWidth: barW,
            itemStyle: {
              borderRadius: barRadius,
            },
            label: {
              fontSize: this.style.chartStyle.fontSize,
              formatter: (series, index) => {
                const unit = this.style.chartStyle?.unit || ''
                const rateType = this.style.chartStyle.rateType
                const dataStr = `{b|${this.series.data ? (this.series.data[series.dataIndex] || '') : ''}${unit}}`

                // 根据输入的值显示同比 环比 文案
                const rateSet = rateType === '' ? '' : rateType === 'yearRate' ? ` {a|同比 ${this.settingRate.data ? (this.settingRate.data[series.dataIndex] || '') : ''}}` : ` {a|环比 ${this.settingRate.data ? (this.settingRate.data[series.dataIndex] || '') : ''}}`
                return dataStr + rateSet
              },
              rich: {
                a: {
                  color: this.style.chartStyle.rateColor,
                  fontWeight: 'bold',
                  fontSize: 12,
                  float: 'right',
                },
                b: {
                  display: 'inline-block',
                  width: 46,
                },
              },
            },
            data: this.series?.max,
          },
        ],
      })

      this.charts.setOption(this.option, true)
      // 是否轮播 tooltips
      if (this.style.chartStyle.carousel && this.series?.data.length) {
        this.tooltipsActive()
        this.mouseEvents()
      } else {
        if (this.timer) {
          clearInterval(this.timer)
          this.charts.dispatchAction({
            type: 'downplay',
            seriesIndex: 0,
            dataIndex: this.index,
          })
          this.index = -1
        }
      }
      // resize
      this.$nextTick(() => {
        this.charts.resize()
      })
    },

  },
}
</script>

<style lang='scss' scoped>
  .rank-list{
    display: flex;
    flex-direction: column;
    &-wrapper {
      position: relative;
      width:100%;
      overflow: visible;
    }
    &-border {
      position: absolute;
      top: 0;
      height: 100%;
      z-index: -1;

    }
    &-title {
      padding:7px 30px;
    }
    .rank-list-container{
      flex: 1;
    }
  }

  </style>
